import React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { createDataTableSelectColumn } from "@/components/ui/data-table-select-column";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { pnlBarFill } from "../../Statistics/components/statsSignedPnlBarUtils";
import { isTradeOpen } from "../../../../utils/positionSemantics";
import {
  TradeLogStatusBadges,
  TradeLogSyncTypeBadge,
  TradeLogTypeBadge,
} from "./tradeLogTradesTableBadges";
import type { TradeLogTableRow } from "./tradeLogTradesTableTypes";
import { TradeLogSortableCell } from "./tradeLogSortableCell";

export interface TradeLogTradesTableColumnOptions {
  hideSelectionColumn?: boolean;
  formatDate: (date: Date | string) => string;
  formatMoney: (value: number) => string;
  formatPercent: (value: number) => string;
  /** True when the header “select all” control was used to select every row on the page. */
  selectAllFromHeader?: boolean;
  onSelectAllFromHeaderChange?: (viaHeader: boolean) => void;
  brokerOpenTradeIdSet?: ReadonlySet<string>;
}

export function createTradeLogTradesTableColumns(
  opts: TradeLogTradesTableColumnOptions,
): ColumnDef<TradeLogTableRow>[] {
  const columns: ColumnDef<TradeLogTableRow>[] = [];

  if (!opts.hideSelectionColumn) {
    columns.push(
      createDataTableSelectColumn<TradeLogTableRow>({
        selectAllFromHeader: opts.selectAllFromHeader === true,
        onSelectAllFromHeaderChange: opts.onSelectAllFromHeaderChange,
        headerDataAttributes: {
          "data-trade-log-select-all-header": "",
        },
      }),
    );
  }

  columns.push(
    {
      id: "symbol",
      accessorKey: "symbol",
      meta: { label: "Symbol" },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Symbol" />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell className="font-medium">{row.original.symbol}</TradeLogSortableCell>
      ),
    },
    {
      id: "type",
      accessorKey: "type",
      meta: { label: "Type" },
      header: "Type",
      cell: ({ row }) => <TradeLogTypeBadge type={row.original.type} />,
      enableSorting: false,
    },
    {
      id: "entryDate",
      accessorKey: "entryDate",
      meta: { label: "Entry Date" },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Entry Date" />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell>{opts.formatDate(row.original.entryDate)}</TradeLogSortableCell>
      ),
    },
    {
      id: "exitDate",
      accessorFn: (row) => row.exitDate,
      meta: { label: "Exit Date" },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Exit Date" />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell>
          {row.original.exitDate ? opts.formatDate(row.original.exitDate) : "—"}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "entryPrice",
      accessorKey: "entryPrice",
      meta: { label: "Entry Price" },
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Entry Price"
          
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell className="font-medium">
          {opts.formatMoney(row.original.entryPrice)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "exitPrice",
      accessorFn: (row) => row.exitPrice,
      meta: { label: "Exit Price" },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Exit Price"  />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell className="font-medium">
          {row.original.exitPrice ? opts.formatMoney(row.original.exitPrice) : "—"}
        </TradeLogSortableCell>
      ),
      enableSorting: false,
    },
    {
      id: "quantity",
      accessorKey: "entryQuantity",
      meta: { label: "Lot Size" },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Lot Size"  />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell  className="font-medium">
          {row.original.entryQuantity}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "netPnL",
      accessorFn: (row) =>
        isTradeOpen(row, opts.brokerOpenTradeIdSet) ? row.floatingPnl : row.netPnL,
      meta: { label: "P&L" },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="P&L" />
      ),
      cell: ({ row }) => {
        const isOpen = isTradeOpen(row.original, opts.brokerOpenTradeIdSet);
        const pnlValue = isOpen ? row.original.floatingPnl : row.original.netPnL;

        if (typeof pnlValue !== "number" || !Number.isFinite(pnlValue)) {
          return <TradeLogSortableCell>—</TradeLogSortableCell>;
        }

        return (
          <TradeLogSortableCell className="font-medium">
            <span data-trade-log-pnl="" style={{ color: pnlBarFill(pnlValue) }}>
              {opts.formatMoney(pnlValue)}
            </span>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "returnPercent",
      accessorFn: (row) => row.returnPercent,
      meta: { label: "Return %" },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Return %" />
      ),
      cell: ({ row }) =>
        typeof row.original.returnPercent === "number" &&
        Number.isFinite(row.original.returnPercent) ? (
          <TradeLogSortableCell className="font-medium">
            {opts.formatPercent(row.original.returnPercent)}
          </TradeLogSortableCell>
        ) : (
          <TradeLogSortableCell>—</TradeLogSortableCell>
        ),
    },
    {
      id: "status",
      accessorKey: "status",
      meta: { label: "Status" },
      header: "Status",
      cell: ({ row }) => (
        <TradeLogStatusBadges
          trade={row.original}
          brokerOpenTradeIdSet={opts.brokerOpenTradeIdSet}
        />
      ),
      enableSorting: false,
    },
    {
      id: "syncType",
      meta: { label: "Sync Type" },
      header: "Sync Type",
      cell: ({ row }) => <TradeLogSyncTypeBadge trade={row.original} />,
      enableSorting: false,
    },
  );

  return columns;
}

/** Reduced column set for mobile — same table chrome, fewer columns to avoid horizontal scroll. */
export function createTradeLogTradesTableMobileColumns(
  opts: Pick<
    TradeLogTradesTableColumnOptions,
    "formatDate" | "formatMoney" | "formatPercent" | "brokerOpenTradeIdSet"
  >,
): ColumnDef<TradeLogTableRow>[] {
  return [
    {
      id: "symbol",
      accessorKey: "symbol",
      meta: { label: "Symbol" },
      header: "Symbol",
      enableSorting: false,
      cell: ({ row }) => (
        <TradeLogSortableCell className="font-medium">
          {row.original.symbol}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "type",
      accessorKey: "type",
      meta: { label: "Type" },
      header: "Type",
      cell: ({ row }) => <TradeLogTypeBadge type={row.original.type} />,
      enableSorting: false,
    },
    {
      id: "exitDate",
      accessorFn: (row) => row.exitDate,
      meta: { label: "Exit Date" },
      header: "Exit",
      enableSorting: false,
      cell: ({ row }) => (
        <TradeLogSortableCell>
          {row.original.exitDate ? opts.formatDate(row.original.exitDate) : "—"}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "netPnL",
      accessorFn: (row) =>
        isTradeOpen(row, opts.brokerOpenTradeIdSet) ? row.floatingPnl : row.netPnL,
      meta: { label: "P&L", align: "end" },
      header: "P&L",
      enableSorting: false,
      cell: ({ row }) => {
        const isOpen = isTradeOpen(row.original, opts.brokerOpenTradeIdSet);
        const pnlValue = isOpen ? row.original.floatingPnl : row.original.netPnL;

        if (typeof pnlValue !== "number" || !Number.isFinite(pnlValue)) {
          return (
            <TradeLogSortableCell align="right" className="tabular-nums">
              —
            </TradeLogSortableCell>
          );
        }

        return (
          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
            <span data-trade-log-pnl="" style={{ color: pnlBarFill(pnlValue) }}>
              {opts.formatMoney(pnlValue)}
            </span>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "status",
      accessorKey: "status",
      meta: { label: "Status" },
      header: "Status",
      cell: ({ row }) => (
        <TradeLogStatusBadges
          trade={row.original}
          brokerOpenTradeIdSet={opts.brokerOpenTradeIdSet}
        />
      ),
      enableSorting: false,
    },
  ];
}
